import React, { useEffect, useState } from "react";
import { flagUrl } from "../../shared/flags";
import s from "./RoyaleCardsSheet.module.scss";

// /debug/royale-cards: ways the start page's Royale card could look. Each is
// drawn at the card's real width with the same made-up Royale (number, clock,
// seats taken, last winner), so they can be told apart on looks alone.
const NO = 47, SEATS = 30, TAKEN = 14, WINNER = { name: "ColdNoodle", flag: "dk" };

const Crown = ({ solid = false }: { solid?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity={solid ? 1 : 0.25} /><path d="M6 21h12" />
  </svg>
);
// The clock with every character in its own fixed cell, so nothing beside it
// shifts as the digits change (the display face has no fixed-width figures).
const Clock = ({ t }: { t: number }) => (
  <b className={s.clock}>{mmss(t).split("").map((ch, k) => <span key={k} className={ch === ":" ? s.colon : s.digit}>{ch}</span>)}</b>
);
const mmss = (t: number) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
const Last = () => (<span className={s.last}>Last won by <img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span>);
const Tag = () => (<span className={s.tag}>Up to <b>30 players</b><i />only one survives</span>);
const Bar = () => (<span className={s.bar}><i style={{ width: `${(TAKEN / SEATS) * 100}%` }} /></span>);
const Seats = () => (<span className={s.seats}>{Array.from({ length: SEATS }, (_, k) => <i key={k} className={k < TAKEN ? s.on : undefined} />)}</span>);
const Name = () => (<span className={s.name}>ROYALE <em>#{NO}</em></span>);

export default function RoyaleCardsSheet() {
  const [t, setT] = useState(137);
  useEffect(() => { const id = setInterval(() => setT((v) => (v > 0 ? v - 1 : 299)), 1000); return () => clearInterval(id); }, []);
  const clock = mmss(t);
  const cards: [string, React.ReactNode][] = [
    ["A. The clock first, a seat for every player (the clock keeps its width)", (
      <div className={`${s.card} ${s.c6}`}><span className={s.shine} />
        <Clock t={t} />
        <div className={s.col}><Name /><Tag /></div>
        <span className={s.crown}><Crown /></span>
        <Seats /><Last />
      </div>
    )],
    ["B. The same, the clock in the middle of the card", (
      <div className={`${s.card} ${s.c6} ${s.midClock}`}><span className={s.shine} />
        <div className={s.col}><Name /><Tag /></div>
        <Clock t={t} />
        <span className={s.crown}><Crown /></span>
        <Seats /><Last />
      </div>
    )],
    ["C. The same, the clock beside the crown behind a thin line", (
      <div className={`${s.card} ${s.c6} ${s.lineClock}`}><span className={s.shine} />
        <div className={s.col}><Name /><Tag /></div>
        <Clock t={t} />
        <span className={s.crown}><Crown /></span>
        <Seats /><Last />
      </div>
    )],
    ["D. Gold block: the crown over the last winner", (
      <div className={`${s.card} ${s.c3} ${s.blockD}`}>
        <span className={s.block}><span className={s.shine} /><Crown solid /><small>Last won by</small><span className={s.who}><img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span></span>
        <div className={s.body}><div className={s.row}><Name /><Clock t={t} /></div><Tag /><Seats /></div>
      </div>
    )],
    ["E. Gold block: the last winner on a dark plate under the crown", (
      <div className={`${s.card} ${s.c3} ${s.blockE}`}>
        <span className={s.block}><span className={s.shine} /><Crown solid /><span className={s.plate}><img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span></span>
        <div className={s.body}><div className={s.row}><Name /><Clock t={t} /></div><Tag /><Seats /></div>
      </div>
    )],
    ["F. Gold block, wide: the crown beside the last winner", (
      <div className={`${s.card} ${s.c3} ${s.blockF}`}>
        <span className={s.block}><span className={s.shine} /><Crown solid /><span className={s.stack}><small>Last won by</small><span className={s.who}><img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span></span></span>
        <div className={s.body}><div className={s.row}><Name /><Clock t={t} /></div><Tag /><Seats /></div>
      </div>
    )],
    ["G. The same as D, but an outlined block instead of a filled one", (
      <div className={`${s.card} ${s.c3} ${s.blockD} ${s.blockG}`}>
        <span className={s.block}><span className={s.shine} /><Crown solid /><small>Last won by</small><span className={s.who}><img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span></span>
        <div className={s.body}><div className={s.row}><Name /><Clock t={t} /></div><Tag /><Seats /></div>
      </div>
    )],
    ["1. Crown beside the clock (in the game now)", (
      <div className={`${s.card} ${s.c1}`}><span className={s.shine} />
        <div className={s.col}><Name /><Tag /></div><b className={s.clock}>{clock}</b><span className={s.crown}><Crown /></span>
        <Bar /><Last />
      </div>
    )],
    ["2. A huge crown behind the right end", (
      <div className={`${s.card} ${s.c2}`}><span className={s.shine} /><span className={s.ghost}><Crown solid /></span>
        <div className={s.col}><Name /><Tag /></div><b className={s.clock}>{clock}</b>
        <Bar /><Last />
      </div>
    )],
    ["3. A gold block with the crown on the left", (
      <div className={`${s.card} ${s.c3}`}>
        <span className={s.block}><span className={s.shine} /><Crown solid /></span>
        <div className={s.body}><div className={s.row}><Name /><b className={s.clock}>{clock}</b></div><Tag /><Bar /><Last /></div>
      </div>
    )],
    ["4. A gold bar at the bottom, like the Play Now button", (
      <div className={`${s.card} ${s.c4}`}>
        <div className={s.top}><div className={s.col}><Tag /><Last /></div><Seats /></div>
        <div className={s.gold}><span className={s.shine} /><b>ROYALE <em>#{NO}</em></b><span className={s.crown}><Crown solid /></span><b className={s.clock}>{clock}</b></div>
      </div>
    )],
    ["5. Centred, the crown on top", (
      <div className={`${s.card} ${s.c5}`}><span className={s.shine} />
        <span className={s.crown}><Crown /></span>
        <span className={s.rule}><i /><Name /><i /></span>
        <b className={s.clock}>{clock}</b><Tag /><Bar /><Last />
      </div>
    )],
    ["7. The crown and who holds it, on the right", (
      <div className={`${s.card} ${s.c7}`}><span className={s.shine} />
        <div className={s.col}><Name /><Tag /><b className={s.clock}>{clock}</b><Bar /></div>
        <div className={s.holder}><span className={s.crown}><Crown solid /></span><small>Last won by</small><span><img src={flagUrl(WINNER.flag)} alt="" /><b>{WINNER.name}</b></span></div>
      </div>
    )],
    ["8. A gold top edge, everything on one line", (
      <div className={`${s.card} ${s.c8}`}><span className={s.shine} />
        <span className={s.crown}><Crown solid /></span>
        <div className={s.col}><Name /><Tag /></div>
        <Last />
        <b className={s.clock}>{clock}</b>
        <Bar />
      </div>
    )],
  ];
  return (
    <div className={s.page}>
      <h1>Royale card</h1>
      {cards.map(([title, card]) => (<section key={title}><h2>{title}</h2>{card}</section>))}
    </div>
  );
}
